import AutoFixNormal from '@mui/icons-material/AutoFixNormal';
import React, { useMemo, useState } from 'react';

import { Button, Tooltip } from '@material-ui/core';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';

import type { FixButtonProps } from '../../../types';
import { ResourceGroupTagDialog } from './ResourceGroupTagDialog';
import type { FailingResourceGroup } from './buildResourceGroupRows';
import { resourceGroupArmId } from './recentTagFixes';
import { getTagKeyForCheck } from './resourceGroupTags';
import { useRecentTagFixes } from './useRecentTagFixes';
import { useTagFixSoftRelease } from './useTagFixSoftRelease';

const humaniseTagKey = (tagKey: string) =>
  tagKey.charAt(0).toUpperCase() + tagKey.slice(1);

export const ResourceGroupTagFix: React.FC<FixButtonProps> = ({
  context,
  performDataRefresh,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const inSoftRelease = useTagFixSoftRelease();

  const tagKey = getTagKeyForCheck(
    context?._checkName ?? context?.check_name ?? '',
  );

  const recentFixes = useRecentTagFixes(tagKey);

  const entries = useMemo((): FailingResourceGroup[] => {
    const groups = context?.untagged_resource_groups;
    if (!Array.isArray(groups)) return [];
    // Entries are `{ account_id, account_name, resource_group }`; older
    // payloads carried the bare resource group name.
    return groups
      .map(group =>
        typeof group === 'string'
          ? { name: group }
          : {
              name: group?.resource_group,
              subscriptionId: group?.account_id || undefined,
              subscriptionName: group?.account_name || undefined,
            },
      )
      .filter(
        (group): group is FailingResourceGroup =>
          typeof group.name === 'string' && !!group.name.trim(),
      );
  }, [context?.untagged_resource_groups]);

  /** When each entry was fixed from this browser, if recently enough to matter. */
  const fixedAt = useMemo(
    () =>
      entries.map(group =>
        group.subscriptionId
          ? recentFixes.get(
              resourceGroupArmId(group.subscriptionId, group.name),
            )
          : undefined,
      ),
    [entries, recentFixes],
  );

  // Passing checks carry no untagged resource groups, so they get no button.
  if (!inSoftRelease || !tagKey || entries.length === 0) return null;

  const tagLabel = humaniseTagKey(tagKey);
  const fixedTimes = fixedAt.filter(
    (time): time is number => time !== undefined,
  );

  const handleClose = ({ appliedCount }: { appliedCount: number }) => {
    setDialogOpen(false);
    if (appliedCount > 0) performDataRefresh?.();
  };

  // Everything here was just tagged; the scorecard has yet to re-read Azure.
  if (fixedTimes.length === entries.length) {
    const lastFixedAt = new Date(Math.max(...fixedTimes)).toLocaleTimeString(
      [],
      { hour: '2-digit', minute: '2-digit' },
    );
    return (
      <Tooltip
        arrow
        title={`Tags were set in Azure at ${lastFixedAt}. The scorecard re-reads Azure about every 3 hours, so this check updates on the next refresh.`}
      >
        <span>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<CheckCircleOutlineIcon />}
            disabled
            aria-label={`${tagLabel} tag fix applied`}
          >
            Fix applied
          </Button>
        </span>
      </Tooltip>
    );
  }

  const pendingNote =
    fixedTimes.length > 0
      ? ` (${fixedTimes.length} of ${entries.length} already tagged, waiting for scorecard refresh)`
      : '';

  return (
    <>
      <Tooltip
        arrow
        title={`Set the missing ${tagKey} tag on these resource groups${pendingNote}`}
      >
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<AutoFixNormal />}
          onClick={() => setDialogOpen(true)}
          aria-label={`Fix ${tagKey} tag`}
        >
          Fix
        </Button>
      </Tooltip>
      {dialogOpen && (
        <ResourceGroupTagDialog
          tagKey={tagKey}
          tagLabel={tagLabel}
          checkLabel={`${tagLabel} Tag Present`}
          resourceGroups={entries}
          teamId={context?.team_id}
          onClose={handleClose}
        />
      )}
    </>
  );
};
